import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/IngramMicro.css";
import ingramLogo from "../../assets/images/partners/ingram-micro-logo.png";

function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const cloudPillars = [
  { icon: "☁️", name: "Cloud Marketplace", desc: "Ingram Micro Cloud Marketplace is one of the world's largest B2B cloud platforms — aggregating 65,000+ cloud services from Microsoft, AWS, Google Cloud, and hundreds of ISVs into a single procurement and billing interface for resellers." },
  { icon: "📊", name: "SaaS Management", desc: "Automated SaaS license management, renewal orchestration, and usage reporting for Microsoft 365, Adobe Creative Cloud, Salesforce, and 500+ SaaS applications through a single partner portal." },
  { icon: "🔄", name: "Lifecycle Management", desc: "End-to-end cloud subscription lifecycle management — from provisioning and billing through renewal and upsell automation — enabling resellers to scale cloud revenue without proportional operations growth." },
  { icon: "🤖", name: "AI & Automation Tools", desc: "AI-powered pricing recommendations, demand forecasting, and churn prediction built into the Ingram Micro Cloud marketplace — helping partners optimize cloud portfolio margins automatically." },
];

const supplyItems = [
  { icon: "🏭", side: "left", label: "Manufacturer", title: "OEM Partnership Network", body: "Ingram Micro maintains direct tier-1 relationships with 3,400+ technology manufacturers worldwide — ensuring product availability, priority allocation during supply constraints, and early access to new product lines before general availability." },
  { icon: "🏢", side: "right", label: "Ingram Micro", title: "Global Distribution Hub", body: "61 distribution centers across North America, Europe, Asia-Pacific, and Latin America provide same-day or next-business-day fulfillment for in-stock inventory — with bonded warehouse capabilities for specialized configurations and international trade compliance." },
  { icon: "🚚", side: "left", label: "Logistics", title: "Advanced Fulfillment Services", body: "Ingram Micro Logistics goes beyond shipping — offering custom kitting, imaging, configuration, asset tagging, and white-label packaging so partners can deliver pre-configured technology directly to end customers under their own brand." },
  { icon: "🤝", side: "right", label: "Reseller", title: "Partner Enablement Layer", body: "Ingram Micro's partner enablement ecosystem includes technical presales support, financial services (net payment terms, leasing, financing), and Ingram Micro Trust X Alliance community — accelerating partner growth at every stage." },
  { icon: "🏢", side: "left", label: "End Customer", title: "Last-Mile Technology Delivery", body: "ALG Data Guard leverages Ingram Micro's supply chain to deliver configured, rack-ready technology directly to customer data centers and office locations — eliminating staging delays and reducing time-to-deployment for infrastructure projects." },
];

const vendors = [
  "Apple", "Microsoft", "Cisco", "Dell Technologies", "HP Inc", "Lenovo", "IBM", "AWS",
  "Fortinet", "Palo Alto", "CrowdStrike", "VMware", "Nutanix", "NetApp", "Pure Storage",
  "Juniper", "F5 Networks", "Veeam", "Barracuda", "Veritas",
  "Apple", "Microsoft", "Cisco", "Dell Technologies", "HP Inc", "Lenovo", "IBM", "AWS",
  "Fortinet", "Palo Alto", "CrowdStrike", "VMware", "Nutanix", "NetApp", "Pure Storage",
];

const presenceStats = [
  { val: "61", label: "Distribution Centers" },
  { val: "170+", label: "Countries Served" },
  { val: "90 Days", label: "Avg Payment Terms Available" },
  { val: "$54B", label: "Annual Revenue" },
  { val: "3,400+", label: "Vendor Partners" },
];

const solutions = [
  { icon: "💻", title: "Technology Lifecycle Management", desc: "End-to-end management of technology assets from procurement through deployment, warranty, and disposal — including asset tagging, ITSM integration, and certified data destruction for decommissioned equipment." },
  { icon: "🔧", title: "Configuration & Imaging Services", desc: "Ingram Micro's Advanced Configuration Centers configure servers, storage, and networking equipment to your specifications — including BIOS settings, firmware updates, RAID configuration, and OS imaging — before shipment." },
  { icon: "💰", title: "Financial Services & Leasing", desc: "Ingram Micro Financial Services provides net payment terms, technology leasing, and financing programs enabling partners to fund large infrastructure projects without depleting client capital budgets." },
  { icon: "🔄", title: "Reverse Logistics & ITAD", desc: "Certified IT asset disposition services including data sanitization to NIST 800-88 standards, responsible recycling, and remarketing — with full chain-of-custody documentation for compliance and audit purposes." },
  { icon: "📦", title: "White-Label Fulfillment", desc: "Ingram Micro drop-ships branded packaging directly to end customers — enabling resellers to deliver large technology orders without touching inventory, reducing handling costs and delivery timelines." },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "Healthcare IT procurement including HIPAA-compliant imaging services, biomedical device sourcing, and clinical workstation configuration for hospitals, clinics, and healthcare networks." },
  { icon: "🏦", name: "Financial Services", desc: "Secure configuration services for financial institution endpoints, dual-control shipping for high-security equipment, and vendor-neutral technology sourcing for banking and insurance organizations." },
  { icon: "🏛️", name: "Government & Education", desc: "TAA-compliant product sourcing, cooperative purchasing contract access (NASPO, SLED), and GSA schedule procurement for federal, state, local, and education sector clients." },
  { icon: "🏭", name: "Manufacturing & Industrial", desc: "OT hardware sourcing for industrial environments, ruggedized endpoint procurement, and supply chain management for manufacturing organizations requiring long-term product availability commitments." },
  { icon: "🛒", name: "Retail & Hospitality", desc: "Large-scale endpoint rollouts for retail chains and hospitality organizations — with standardized imaging, configuration, and coordinated multi-site deployment scheduling." },
  { icon: "☁️", name: "MSP & Cloud Resellers", desc: "Complete MSP supply chain solution including cloud marketplace access, hardware fulfillment, financing, and back-office support enabling managed service providers to simplify their vendor ecosystem." },
];

const reasons = [
  { icon: "🤝", title: "Direct Ingram Micro Partner Relationship", desc: "ALG Data Guard's authorized reseller status provides direct access to Ingram Micro's full catalog, preferred pricing, and technical presales resources for complex project quoting." },
  { icon: "⚡", title: "Next-Day Availability for In-Stock Items", desc: "Leveraging Ingram Micro's regional distribution centers, ALG Data Guard delivers in-stock technology within 1–2 business days to most North American locations." },
  { icon: "💰", title: "Financial Flexibility for Large Projects", desc: "Ingram Micro Financial Services enables ALG Data Guard to offer flexible payment terms, leasing, and financing options that align technology investments with client budget cycles." },
  { icon: "🔧", title: "Pre-Configured, Deployment-Ready Equipment", desc: "ALG Data Guard works with Ingram Micro's Advanced Configuration Centers to deliver servers and networking equipment pre-imaged, rack-labeled, and deployment-ready — reducing on-site time." },
];

const faqData = [
  { q: "What is Ingram Micro and why does it matter for technology procurement?", a: "Ingram Micro is the world's largest technology distributor — a wholesale intermediary between technology manufacturers (Dell, Cisco, Microsoft, HP) and resellers (VARs, MSPs, system integrators) like ALG Data Guard. Rather than purchasing directly from manufacturers — which often requires large volume commitments, complex credit applications, and direct vendor relationships — resellers purchase through Ingram Micro's catalog of 200,000+ products at competitive pricing with flexible payment terms. For end customers, this means access to a broader product selection, faster availability, and flexible financing not typically available when purchasing directly from manufacturers. Ingram Micro's 61 distribution centers ensure fast regional fulfillment, and their advanced configuration services mean technology arrives pre-configured rather than requiring extensive on-site setup time." },
  { q: "How does Ingram Micro Cloud Marketplace benefit organizations buying cloud services?", a: "Ingram Micro Cloud Marketplace aggregates 65,000+ cloud services from Microsoft (Microsoft 365, Azure), AWS, Google Cloud, and hundreds of SaaS vendors into a single procurement, billing, and management interface. For resellers like ALG Data Guard, this means managing all cloud subscriptions for all clients through one platform — with consolidated invoicing, automated provisioning, and usage reporting. For end customers, this translates to a single invoice for all cloud services (eliminating dozens of direct vendor invoices), a single point of contact for subscription changes, and access to competitive pricing through Ingram Micro's volume purchasing agreements with cloud vendors. The Cloud Marketplace also enables bundled offers combining cloud subscriptions with managed services and hardware." },
  { q: "What are Ingram Micro's configuration services and how do they save deployment time?", a: "Ingram Micro's Advanced Configuration Centers provide hardware customization before shipment — including server BIOS configuration, firmware updates, RAID and storage configuration, OS imaging, memory and drive installation, asset tagging, and custom packaging. For organizations deploying multiple identical servers or workstations, this means equipment arrives ready to rack or deploy rather than requiring technicians to configure each unit on-site. This is particularly valuable for multi-site rollouts where ALG Data Guard coordinates configuration of 50–500+ devices in Ingram Micro's facilities, then coordinates drop-shipment directly to remote locations — eliminating staging time and reducing the number of technicians required per site." },
  { q: "What financing options does Ingram Micro Financial Services offer?", a: "Ingram Micro Financial Services provides several financing programs: Net terms (30, 60, or 90 days) allowing resellers to deploy technology before payment is due — improving cash flow for project-based businesses. Technology leasing structures capital expenditures as operating expenses — enabling clients to avoid large upfront purchases and refresh technology on a predictable schedule. Installment purchase plans spread large infrastructure investments over 24–60 months. For government and education clients, Ingram Micro supports cooperative purchasing contracts through NASPO, E&I, and state-specific programs. ALG Data Guard works with Ingram Micro Financial Services to structure the best financing approach for each client's project scope and budget situation." },
  { q: "How does ALG Data Guard leverage Ingram Micro for IT projects?", a: "ALG Data Guard uses Ingram Micro as a primary procurement partner for hardware and software projects. For infrastructure deployments, we source servers, storage, and networking equipment through Ingram Micro's catalog — leveraging their pricing, availability, and configuration services. For software licensing, we procure Microsoft, VMware, and other vendor licenses through Ingram Micro's volume licensing programs. For cloud services, we use Ingram Micro Cloud Marketplace to provision and manage client Microsoft 365, Azure, and SaaS subscriptions. For large multi-site rollouts, we leverage Ingram Micro's configuration centers and drop-ship capabilities to deliver pre-configured equipment directly to client locations across the country. For clients requiring financing, we structure Ingram Micro Financial Services agreements to fit project budgets." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function IngramMicro() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="im-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={ingramLogo} alt="Ingram Micro" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Global Technology Distribution Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Ingram Micro</h1>
            <p className="pd-identity-desc">
              Ingram Micro is the world's largest technology distributor — a $54 billion global enterprise connecting 3,400+ technology manufacturers to 200,000+ resellers across 170 countries. Through hardware distribution, cloud marketplace services, advanced configuration, logistics, and financial solutions, Ingram Micro is the supply chain backbone that enables technology resellers and system integrators to deliver enterprise IT at scale. ALG Data Guard is an authorized Ingram Micro partner leveraging this global infrastructure for client technology procurement.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="im-hero">
        <div className="im-hero__bg" aria-hidden="true" />
        <div className="im-hero__overlay" aria-hidden="true" />
        <div className="im-hero__inner">
          <div className="im-hero__eyebrow">
            <div className="im-hero__eyebrow-line" />
            <span className="im-hero__eyebrow-text">Ingram Micro Authorized Partner</span>
          </div>
          <h2 className="im-display im-hero__title">
            The World's Technology<br /><span>Distribution Partner</span>
          </h2>
          <p className="im-hero__body">
            Ingram Micro connects the global technology industry — from OEM manufacturers to enterprise end users — through a $54 billion distribution network spanning 61 fulfillment centers, 170 countries, and 3,400+ vendor relationships. ALG Data Guard leverages this global infrastructure to source, configure, and deliver technology for organizations of every scale.
          </p>
          <div className="im-hero__actions">
            <button className="im-btn im-btn--blue" onClick={() => navigate("/contact")}>
              Start a Technology Order <ArrowIcon />
            </button>
            <button className="im-btn im-btn--ghost" onClick={() => navigate("/contact")}>
              Cloud Marketplace Inquiry
            </button>
          </div>
          <div className="im-hero__bar">
            {[
              { val: "$54B+", label: "Annual Revenue" },
              { val: "170+", label: "Countries Served" },
              { val: "3,400+", label: "Vendor Relationships" },
              { val: "61", label: "Global Fulfillment Centers" },
            ].map(s => (
              <div key={s.val}>
                <div className="im-hero__bar-val">{s.val}</div>
                <div className="im-hero__bar-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Distribution Narrative ── */}
      <section className="im-distribution">
        <div className="im-shell">
          <div className="im-distribution__inner">
            <img
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=900&q=80"
              alt="Global distribution network"
              className="im-distribution__img"
            />
            <div className="im-distribution__content">
              <div className="im-label im-label--blue"><span className="im-dot" /> Global Distribution Network</div>
              <h2 className="im-distribution__title">The Infrastructure Behind Enterprise Technology</h2>
              <p className="im-distribution__body">
                Ingram Micro's global distribution network is one of the most sophisticated logistics systems in the technology industry. With 61 distribution centers strategically positioned across continents, Ingram Micro ensures that enterprise-class hardware — servers, storage, networking, endpoints, and accessories from 3,400+ vendors — reaches resellers and end customers within days, not weeks.
              </p>
              <p className="im-distribution__body">
                For ALG Data Guard clients, this means enterprise technology projects that might otherwise face 6–8 week lead times are frequently deliverable within 5–10 business days for in-stock items — accelerating time-to-deployment for critical infrastructure initiatives.
              </p>
              <div className="im-distribution__facts">
                <div className="im-fact">
                  <div className="im-fact__val">200K+</div>
                  <div className="im-fact__label">Products in Catalog</div>
                </div>
                <div className="im-fact">
                  <div className="im-fact__val">200K+</div>
                  <div className="im-fact__label">Reseller Partners</div>
                </div>
                <div className="im-fact">
                  <div className="im-fact__val">65K+</div>
                  <div className="im-fact__label">Cloud Services on Marketplace</div>
                </div>
                <div className="im-fact">
                  <div className="im-fact__val">#1</div>
                  <div className="im-fact__label">Largest Technology Distributor</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Cloud Marketplace ── */}
      <section className="im-cloud">
        <div className="im-shell">
          <div className="im-cloud__inner">
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80"
              alt="Ingram Micro Cloud Marketplace"
              className="im-cloud__img"
            />
            <div>
              <div className="im-label im-label--dark"><span className="im-dot" /> Cloud Marketplace</div>
              <h2 className="im-cloud__title">One Marketplace. 65,000 Cloud Services.</h2>
              <p className="im-cloud__body">Ingram Micro Cloud is one of the world's largest cloud marketplaces — aggregating Microsoft, AWS, Google Cloud, and hundreds of ISV SaaS applications into a single procurement and management platform. For resellers, it means managing all client cloud subscriptions from one consolidated interface with automated provisioning and unified billing.</p>
              <div className="im-cloud__pillars">
                {cloudPillars.map(p => (
                  <div key={p.name} className="im-cloud-pillar">
                    <span className="im-cloud-pillar__icon">{p.icon}</span>
                    <div>
                      <div className="im-cloud-pillar__name">{p.name}</div>
                      <p className="im-cloud-pillar__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="im-btn im-btn--ghost" onClick={() => navigate("/contact")}>
                  Explore Cloud Marketplace <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Vendor Ecosystem ── */}
      <section className="im-vendors">
        <div className="im-shell">
          <div className="im-vendors__inner">
            <div className="im-vendors__head">
              <div className="im-label im-label--dark" style={{ margin:"0 auto 1rem" }}><span className="im-dot" /> Vendor Ecosystem</div>
              <h2 className="im-title">3,400+ Vendors. One Partner.</h2>
              <p className="im-vendors__sub">Ingram Micro's vendor relationships span every technology category — giving ALG Data Guard access to the industry's broadest product catalog at competitive pricing.</p>
            </div>
            <div className="im-vendors__marquee-wrap">
              <div className="im-vendors__marquee">
                {vendors.map((v, i) => <div key={`${v}-${i}`} className="im-vendor-chip">{v}</div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Supply Chain Timeline ── */}
      <section className="im-supply">
        <div className="im-shell">
          <div className="im-supply__head">
            <div className="im-label im-label--blue" style={{ margin:"0 auto 1rem" }}><span className="im-dot" /> Supply Chain</div>
            <h2 className="im-title">From Manufacturer to Your Data Center</h2>
            <p className="im-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>Ingram Micro's supply chain optimizes every step of the technology journey — from OEM production through last-mile delivery to your organization.</p>
          </div>
          <div className="im-supply__timeline">
            {supplyItems.map((item, i) => (
              <div key={item.title} className="im-supply-item">
                {item.side === "left" ? (
                  <>
                    <div className="im-supply-item__content">
                      <div className="im-supply-item__label">{item.label}</div>
                      <h3 className="im-supply-item__title">{item.title}</h3>
                      <p className="im-supply-item__body">{item.body}</p>
                    </div>
                    <div className="im-supply-item__icon">{item.icon}</div>
                    <div className="im-supply-item__empty" />
                  </>
                ) : (
                  <>
                    <div className="im-supply-item__empty" />
                    <div className="im-supply-item__icon">{item.icon}</div>
                    <div className="im-supply-item__content im-supply-item__content--right">
                      <div className="im-supply-item__label">{item.label}</div>
                      <h3 className="im-supply-item__title">{item.title}</h3>
                      <p className="im-supply-item__body">{item.body}</p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Worldwide Presence ── */}
      <section className="im-presence">
        <div className="im-shell">
          <div className="im-presence__inner">
            <div className="im-label im-label--dark" style={{ margin:"0 auto 1rem" }}><span className="im-dot" /> Worldwide Presence</div>
            <h2 className="im-presence__title">Global Reach. Local Expertise.</h2>
            <p className="im-presence__sub">Ingram Micro's global footprint means enterprise technology reaches organizations in 170 countries with the same efficiency and reliability, whether deploying in Dallas or Dubai.</p>
            <div className="im-presence__stats">
              {presenceStats.map(s => (
                <div key={s.val} className="im-presence-stat">
                  <div className="im-presence-stat__val">{s.val}</div>
                  <div className="im-presence-stat__label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Business Solutions ── */}
      <section className="im-solutions">
        <div className="im-shell">
          <div className="im-solutions__inner">
            <img
              src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=900&q=80"
              alt="Ingram Micro business solutions"
              className="im-solutions__img"
            />
            <div>
              <div className="im-label im-label--blue"><span className="im-dot" /> Business Solutions</div>
              <h2 className="im-title">Beyond Distribution — Complete Technology Solutions</h2>
              <p className="im-sub" style={{ marginBottom:"2rem" }}>Ingram Micro's value-added services transform raw technology procurement into complete business solutions — with configuration, financing, logistics, and lifecycle management included.</p>
              <div className="im-solutions__list">
                {solutions.map(s => (
                  <div key={s.title} className="im-solution-item">
                    <div className="im-solution-item__head">
                      <span className="im-solution-item__icon">{s.icon}</span>
                      <h3 className="im-solution-item__title">{s.title}</h3>
                    </div>
                    <p className="im-solution-item__desc">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="im-industries">
        <div className="im-shell">
          <div className="im-industries__head">
            <div className="im-label im-label--blue"><span className="im-dot" /> Industry Solutions</div>
            <h2 className="im-title">Technology Supply Chain for Every Vertical</h2>
            <p className="im-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Ingram Micro provides specialized procurement pathways for regulated and specialized industries — with compliance documentation, configured hardware, and cooperative purchasing access.</p>
          </div>
          <div className="im-industries__list">
            {industries.map(ind => (
              <div key={ind.name} className="im-industry-item">
                <span className="im-industry-item__icon">{ind.icon}</span>
                <h3 className="im-industry-item__name">{ind.name}</h3>
                <p className="im-industry-item__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why ALG ── */}
      <section className="im-why">
        <div className="im-shell">
          <div className="im-why__inner">
            <div>
              <div className="im-label im-label--dark"><span className="im-dot" /> Why ALG Data Guard</div>
              <h2 className="im-why__title">The Ingram Micro Advantage, <span>Delivered by ALG</span></h2>
              <p className="im-why__body">As an authorized Ingram Micro partner, ALG Data Guard provides clients with competitive technology pricing, access to the world's largest hardware and cloud catalog, flexible financing options, and pre-configured equipment delivery — all through a single trusted technology advisor.</p>
              <div className="im-why__reasons">
                {reasons.map(r => (
                  <div key={r.title} className="im-reason">
                    <span className="im-reason__icon">{r.icon}</span>
                    <div>
                      <div className="im-reason__title">{r.title}</div>
                      <p className="im-reason__desc">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="im-btn im-btn--blue" onClick={() => navigate("/contact")}>
                  Start a Technology Project <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=900&q=80"
              alt="Technology partnership"
              className="im-why__img"
            />
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="im-faq">
        <div className="im-shell--narrow">
          <div className="im-faq__head">
            <div className="im-label im-label--blue" style={{ margin:"0 auto 1rem" }}><span className="im-dot" /> FAQs</div>
            <h2 className="im-title">Ingram Micro Questions, Answered</h2>
            <p className="im-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert answers about Ingram Micro distribution, cloud marketplace, and how ALG Data Guard leverages this global infrastructure for your projects.</p>
          </div>
          <div className="im-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`im-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="im-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="im-faq-item__q">{faq.q}</span>
                  <span className="im-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="im-faq-item__answer"><div className="im-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="im-cta">
        <div className="im-cta__bg" aria-hidden="true" />
        <div className="im-cta__overlay" aria-hidden="true" />
        <div className="im-shell">
          <div className="im-cta__inner">
            <div className="im-label im-label--dark"><span className="im-dot" /> Get Started</div>
            <h2 className="im-cta__title">World-Class Technology Procurement, <span>Made Simple</span></h2>
            <p className="im-cta__sub">Whether you need hardware for a data center refresh, cloud licenses for a Microsoft 365 deployment, or a complete technology supply chain for a multi-site infrastructure project — ALG Data Guard's Ingram Micro partnership delivers the products, pricing, and logistics your organization requires.</p>
            <div className="im-cta__actions">
              <button className="im-btn im-btn--blue" onClick={() => navigate("/contact")}>
                Request a Technology Quote <ArrowIcon />
              </button>
              <button className="im-btn im-btn--ghost" onClick={() => navigate("/contact")}>
                Explore Cloud Marketplace
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
